import { useMemo } from 'react';
import { useTrustsQuery } from './api/useTrustsQuery';
import { usePersonalInfoQuery } from './api/usePersonalInfoQuery';
import { combineQueryResults } from '../../CommonUtils/combineQueryResults';
import { useEstatePlanningPersonalInfoQuery } from './api/useEstatePlanningPersonalInfoQuery';
import { PersonalInfoContext } from './PersonalInfo.meta';

interface Params {
  showEstatePlanning?: boolean;
  context?: PersonalInfoContext;
}

export const usePersonalInfo = (
  { showEstatePlanning, context }: Params = { showEstatePlanning: false },
) => {
  const personalInfoQueryResult = usePersonalInfoQuery({ enabled: !showEstatePlanning });
  const trustsQueryResult = useTrustsQuery({
    enabled: !showEstatePlanning && context !== PersonalInfoContext.CharitableGivingWorkflow,
  });

  const estatePlanningPersonalInfo = useEstatePlanningPersonalInfoQuery({
    enabled: showEstatePlanning,
  });

  const data = useMemo(() => {
    if (showEstatePlanning) {
      if (!estatePlanningPersonalInfo.data) {
        return null;
      }

      return estatePlanningPersonalInfo.data;
    }

    if (context === PersonalInfoContext.CharitableGivingWorkflow) {
      if (!personalInfoQueryResult.data) {
        return null;
      }

      return {
        PersonalInfo: personalInfoQueryResult.data,
      };
    }

    if (!personalInfoQueryResult.data) {
      return null;
    }

    if (!trustsQueryResult.data) {
      return null;
    }

    return {
      PersonalInfo: personalInfoQueryResult.data,
      Trusts: {
        ...trustsQueryResult.data,
        TrusteeOption: trustsQueryResult.data.TrusteeOption.map(trustee => ({
          ...trustee,
          rightLabel: trustee.value === 0 ? 'Principal' : trustee.value === 1 ? 'Partner' : 'Other',
        })),
        GrantorOptions: trustsQueryResult.data.GrantorOptions.map(trustee => ({
          ...trustee,
          rightLabel: trustee.value === 0 ? 'Principal' : trustee.value === 1 ? 'Partner' : 'Other',
        })),
      },
    };
  }, [
    context,
    estatePlanningPersonalInfo.data,
    personalInfoQueryResult.data,
    showEstatePlanning,
    trustsQueryResult.data,
  ]);

  return {
    ...combineQueryResults([personalInfoQueryResult, trustsQueryResult]),
    data,
  };
};
